import { Dialog, DialogContent, TextField, Box, InputAdornment } from "@mui/material";
import { DesktopDatePicker } from "@mui/x-date-pickers";
import isEqual from "lodash/isEqual";
import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { v4 as uuidv4 } from "uuid";

import { Props, State } from "./interfaces";
import { FieldsContainer } from "./styles";

import ConfirmationChangesDialog from "../../../../containers/ConfirmationChangesDialog/ConfirmationChangesDialog";
import DialogActions from "../../../../containers/DialogActions/DialogActions";
import DialogTitle from "../../../../containers/DialogTitle/DialogTitle";
import LoadingOverlay from "../../../../containers/LoadingOverlay/LoadingOverlay";

import { CandidateSkillRef } from "../../../../models/recruiting/candidate/skills/candidate-skill-ref";
import { SkillFilter } from "../../../../models/recruiting/candidate/skills/skill-filter";
import AddEditButton from "../../../../reusable-components/AddEditButton/AddEditButton";
import AutocompleteDropdown from "../../../../reusable-components/AutocompleteDropdown/AutocompleteDropdown";
import { saveCandidate } from "../../../../services/recruiting/candidate/candidate-api.service";
import { SkillLevelsApiService } from "../../../../services/recruiting/candidate/skills/skill-levels/skill-levels-api.service";
import { SkillsApiService } from "../../../../services/recruiting/candidate/skills/skills-api.service";
import { SKILLS_CATEGORIES } from "../../../../utils/constants/constants";
import { convertDateToUTC, isValidDate } from "../../../../utils/time-utils";

export const AddEditSkills = ({
  effect = "add",
  asLink = false,
  linkText,
  item,
  handleNotifyParent,
  buttonVariant,
  children,
  skillCategory,
  candidate,
}: Props) => {
  const { t } = useTranslation();
  const tRP = "pages.system_settings.tables.candidate_skills";

  const DEFAULT_ITEM: Partial<CandidateSkillRef> = useMemo(() => {
    return {
      skill: item?.skill ?? null,
      date: item?.date ? item.date : new Date(),
      skillLevel: item?.skillLevel ?? null,
      yearsOfExperience: item?.yearsOfExperience ?? 0,
    };
  }, [item]);

  const INITIAL_STATE: State = useMemo(() => {
    return {
      isDialogOpen: false,
      loading: false,
      currCandidateSkill: item ?? (DEFAULT_ITEM as CandidateSkillRef),
      isCCDOpen: false,
      areFieldsValidated: effect === "edit",
    };
  }, [effect, DEFAULT_ITEM, item]);

  const [state, setState] = useState<State>({
    ...INITIAL_STATE,
  });

  const handleChangeField = useCallback(
    (field: keyof CandidateSkillRef, value: CandidateSkillRef[keyof CandidateSkillRef]) => {
      setState((state) => ({
        ...state,
        currCandidateSkill: {
          ...state.currCandidateSkill,
          [field]: value,
        },
      }));
    },
    []
  );

  const handleChangeYears = ({ target: { value } }: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    handleChangeField("yearsOfExperience", Number(value));
  };

  const handleSave = useCallback(async () => {
    const arrWithSkills =
      effect === "edit"
        ? candidate.skills.map((skill) => {
            if (item?.id === skill.id) {
              return { ...skill, ...state.currCandidateSkill, date: skill.date ? convertDateToUTC(skill.date) : null };
            } else {
              return skill;
            }
          })
        : [...candidate.skills, { ...state.currCandidateSkill, id: uuidv4() }];

    setState((state) => ({ ...state, loading: true }));

    try {
      const response = await saveCandidate({
        ...candidate,
        skills: [...arrWithSkills],
      });

      if (!response.error) {
        effect === "add" && setState({ ...INITIAL_STATE });
        effect === "edit" &&
          setState((state) => ({
            ...state,
            isDialogOpen: false,
            loading: false,
            isCCDOpen: false,
          }));
        handleNotifyParent();
      }
    } catch (error) {
      setState((state) => ({
        ...state,
        loading: false,
      }));
      handleNotifyParent();
    }
  }, [INITIAL_STATE, effect, handleNotifyParent, state.currCandidateSkill, item?.id, candidate]);

  const handleDelete = useCallback(async () => {
    const arrWithSkills = candidate.skills.filter((skill) => skill.id !== item?.id);

    setState((state) => ({ ...state, loading: true }));

    try {
      const response = await saveCandidate({
        ...candidate,
        skills: [...arrWithSkills],
      });

      if (!response.error) {
        effect === "add" && setState({ ...INITIAL_STATE });
        effect === "edit" &&
          setState((state) => ({
            ...state,
            isDialogOpen: false,
            loading: false,
            isCCDOpen: false,
          }));
        handleNotifyParent();
      }
    } catch (error) {
      setState((state) => ({
        ...state,
        loading: false,
      }));
      handleNotifyParent();
    }
  }, [INITIAL_STATE, effect, handleNotifyParent, state.currCandidateSkill, item?.id, candidate]);

  const handleDiscardChanges = useCallback(() => {
    setState({ ...INITIAL_STATE });
  }, [INITIAL_STATE]);

  const resetAndCloseDialog = (): void => {
    if (!isEqual(state.currCandidateSkill, INITIAL_STATE.currCandidateSkill)) {
      setState((state) => ({
        ...state,
        isCCDOpen: true,
      }));
    } else {
      handleDiscardChanges();
    }
  };

  const areFieldsValidated = useCallback((): boolean => {
    return (
      !!state.currCandidateSkill.skill &&
      !!isValidDate(state.currCandidateSkill.date) &&
      !!state.currCandidateSkill.skillLevel
    );
  }, [state.currCandidateSkill]);

  useEffect(() => {
    setState((state) => ({
      ...state,
      areFieldsValidated: areFieldsValidated(),
    }));
  }, [state.currCandidateSkill, areFieldsValidated]);

  useEffect(() => {
    !!item &&
      setState((state) => ({
        ...state,
        currCandidateSkill: item,
      }));

    return () => {
      if (effect === "add") setState({ ...INITIAL_STATE });
    };
  }, [item, effect, INITIAL_STATE]);

  const alreadyExistingSkills = useMemo(() => {
    return candidate.skills.map((item) => item?.skill?.id);
  }, [candidate.skills]);

  return (
    <>
      {children ? (
        <div
          onClick={() => {
            setState((state) => ({ ...state, isDialogOpen: true }));
          }}
        >
          {children}
        </div>
      ) : (
        <AddEditButton
          title={t(`buttons.${effect}`)}
          effect={effect}
          asLink={asLink}
          linkText={linkText}
          onClick={() => {
            setState((state) => ({ ...state, isDialogOpen: true }));
          }}
          {...(buttonVariant === "text" && { buttonText: "Edit" })}
        />
      )}
      <Dialog
        open={state.isDialogOpen}
        onClose={resetAndCloseDialog}
        fullWidth
      >
        <LoadingOverlay loading={state.loading}>
          <DialogTitle
            title={effect === "add" ? t(`${tRP}.title_for_add_component`) : t(`${tRP}.title_for_edit_component`)}
            handleClose={resetAndCloseDialog}
          />
          <DialogContent>
            <FieldsContainer>
              <Box>
                <AutocompleteDropdown
                  labelKey={`${t(`${tRP}.fields.skills`)}`}
                  field={"skill"}
                  serviceApi={new SkillsApiService()}
                  handleChange={handleChangeField}
                  selectedValue={state.currCandidateSkill.skill}
                  propsFilter={
                    {
                      notIds: alreadyExistingSkills,
                      categoryId: skillCategory.id,
                    } as SkillFilter
                  }
                  required
                />

                <AutocompleteDropdown
                  labelKey={`${t(`${tRP}.fields.skill_level`)}`}
                  field={"skillLevel"}
                  serviceApi={new SkillLevelsApiService()}
                  handleChange={handleChangeField}
                  selectedValue={state.currCandidateSkill.skillLevel}
                  fieldProp={skillCategory.id === SKILLS_CATEGORIES.language.id ? "altName" : "name"}
                  required
                />
              </Box>

              <Box>
                <DesktopDatePicker
                  inputFormat="dd.MM.yyyy"
                  mask="__.__.____"
                  label={`${t(`${tRP}.fields.date`)}`}
                  value={state.currCandidateSkill?.date}
                  onChange={(newValue) => {
                    handleChangeField("date", newValue);
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      required
                    />
                  )}
                />
                <TextField
                  label={`${t(`${tRP}.fields.years_of_experience`)}`}
                  type="number"
                  name="yearsOfExperience"
                  value={state.currCandidateSkill.yearsOfExperience}
                  onChange={handleChangeYears}
                  InputProps={{
                    inputProps: { min: 0, step: 1 },
                    endAdornment: <InputAdornment position="end">{"years"}</InputAdornment>,
                  }}
                  onBlur={handleChangeYears}
                />
              </Box>
            </FieldsContainer>
          </DialogContent>
          <DialogActions
            handleSave={handleSave}
            handleDelete={handleDelete}
            canBeDeleted={!!item?.id}
            disabledSaveButton={!areFieldsValidated()}
            handleCancel={resetAndCloseDialog}
          />
        </LoadingOverlay>
      </Dialog>

      <ConfirmationChangesDialog
        open={state.isCCDOpen}
        handleCancel={() => setState({ ...state, isCCDOpen: false })}
        handleDiscard={handleDiscardChanges}
      />
    </>
  );
};
